import {
  ActiveFilters,
  CountyRef,
  NetworkPortfolioManagementProjectActionStatus,
  ProjectActionFilter,
  projectStatusFilterOrder,
  selectableProjectTypes,
  SpecialtyGroupRef,
} from "@app/network-portfolio-management/interfaces";

/**
 * Returns a fresh empty ActiveFilters value. Each call yields a new instance, so callers can never
 * accidentally share or mutate a singleton.
 */
export function emptyActiveFilters(): ActiveFilters {
  return {
    linesOfBusinessIds: [],
    projectAction: null,
    projectStatuses: [],
    projectTypes: [],
    projectUtilizationCategoryIds: [],
    searchTerm: "",
    serviceAreaCounties: [],
    serviceAreaStates: [],
    specialtyGroups: [],
    tagIds: [],
    templateNames: [],
  };
}

const PROJECT_ACTIONS: readonly NetworkPortfolioManagementProjectActionStatus[] = [
  NetworkPortfolioManagementProjectActionStatus.Run,
  NetworkPortfolioManagementProjectActionStatus.Publish,
  NetworkPortfolioManagementProjectActionStatus.Archive,
];

function asProjectActionFilter(value: unknown): ProjectActionFilter {
  return PROJECT_ACTIONS.includes(value as NetworkPortfolioManagementProjectActionStatus)
    ? (value as NetworkPortfolioManagementProjectActionStatus)
    : null;
}

function asFilteredArray<T>(value: unknown, allowed: readonly T[]): T[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is T => allowed.includes(v as T));
}

export interface DefaultView {
  readonly npmFilteredViewId: number | null;
  readonly filters: ActiveFilters;
}

const DEFAULT_VIEW_KEY_SUFFIX = "_npm-default-view";

function defaultViewKey(userId: number | undefined): string {
  return `${userId}${DEFAULT_VIEW_KEY_SUFFIX}`;
}

/**
 * Reads the persisted default view for the given user. Returns All Projects (null id, empty filters) when
 * nothing is stored, when the stored JSON is corrupt, or when a filter was cached in a shape this build no
 * longer understands - in that last case the caller falls back to the server's default view, which is
 * authoritative and carries its own load-error reporting.
 */
export function getDefaultView(userId: number | undefined): DefaultView {
  try {
    const raw = window.localStorage.getItem(defaultViewKey(userId));
    if (!raw) return { filters: emptyActiveFilters(), npmFilteredViewId: null };
    const parsed = JSON.parse(raw) as { filters?: unknown; npmFilteredViewId?: unknown; };
    const id =
      typeof parsed.npmFilteredViewId === "number" && Number.isFinite(parsed.npmFilteredViewId)
        ? parsed.npmFilteredViewId
        : null;
    const filtersRaw = (parsed.filters ?? {}) as Partial<Record<keyof ActiveFilters, unknown>>;
    // A blob written before this filter was renamed carries `specialties` instead of `specialtyGroups`. Reading
    // only the current key would silently accept the cached view with an empty specialty-group selection, which
    // is the same silent-degradation this function exists to avoid - so the legacy key is unreadable too.
    if ("specialties" in (filtersRaw as Record<string, unknown>)) {
      return { filters: emptyActiveFilters(), npmFilteredViewId: null };
    }
    const specialtyGroups = asSpecialtyArrayOrNull(filtersRaw.specialtyGroups);
    if (specialtyGroups === null) return { filters: emptyActiveFilters(), npmFilteredViewId: null };
    return {
      filters: {
        linesOfBusinessIds: asNumberArray(filtersRaw.linesOfBusinessIds),
        projectAction: asProjectActionFilter(filtersRaw.projectAction),
        projectStatuses: asFilteredArray(filtersRaw.projectStatuses, projectStatusFilterOrder),
        projectTypes: asFilteredArray(filtersRaw.projectTypes, selectableProjectTypes),
        projectUtilizationCategoryIds: asNumberArray(filtersRaw.projectUtilizationCategoryIds),
        searchTerm: typeof filtersRaw.searchTerm === "string" ? filtersRaw.searchTerm : "",
        serviceAreaCounties: asCountyArray(filtersRaw.serviceAreaCounties),
        serviceAreaStates: asStringArray(filtersRaw.serviceAreaStates),
        specialtyGroups,
        tagIds: asNumberArray(filtersRaw.tagIds),
        templateNames: asStringArray(filtersRaw.templateNames),
      },
      npmFilteredViewId: id,
    };
  } catch {
    return { filters: emptyActiveFilters(), npmFilteredViewId: null };
  }
}

/**
 * Persists the user's default view to localStorage. Pass null for npmFilteredViewId to record
 * All Projects as the default.
 */
export function setDefaultView(userId: number | undefined, npmFilteredViewId: number | null, filters: ActiveFilters): void {
  window.localStorage.setItem(defaultViewKey(userId), JSON.stringify({ filters, npmFilteredViewId }));
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string");
}

function asNumberArray(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is number => typeof v === "number" && Number.isFinite(v));
}

function asCountyArray(value: unknown): CountyRef[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((v): v is { state: unknown; county: unknown } =>
      typeof v === "object" && v !== null && "state" in v && "county" in v)
    .filter((v) => typeof v.state === "string" && typeof v.county === "string")
    .map((v) => ({ county: v.county as string, state: v.state as string }));
}

/**
 * Parses stored specialties, returning null when the value is not the current {code, name} shape - e.g. a
 * default view cached before specialties became structured, when they were a plain string[].
 *
 * Null means "unreadable", not "empty": the caller discards the whole cached blob rather than applying a
 * partially-readable default. Salvaging the readable half would silently drop the user's specialty selection with
 * no warning, which is exactly the failure this feature's stale-view banner exists to surface. The cache is only
 * ever a fast-paint copy of the server's default view, so discarding it just defers to the authoritative copy.
 */
function asSpecialtyArrayOrNull(value: unknown): SpecialtyGroupRef[] | null {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) return null;

  const groups: SpecialtyGroupRef[] = [];
  for (const entry of value) {
    if (typeof entry !== "object" || entry === null) return null;
    const { code, name } = entry as { code?: unknown; name?: unknown };
    if (typeof code !== "string" || typeof name !== "string") return null;
    groups.push({ code, name });
  }
  return groups;
}
